import { Controller } from "@hotwired/stimulus"

// The chip follows the ticks as they change: one pick names itself with a ✕,
// several show the filter's name and a count, and either takes the chevron's
// place and darkens the chip's edge. Picks also repeat at the top of the menu,
// and unticking either copy unticks both. Clearing from the chip lets the page
// react, the same as unticking one by one.
//
// A single-select filter closes as soon as an option is picked; a multi-select
// one stays open so several can be ticked in a row. Either way it announces
// "select-filter:closed" when it closes, for a page that acts once the choice
// is settled.
export default class extends Controller {
  static targets = ["chip", "count", "clear", "chevron", "label", "picked", "pickedRows"]

  connect() {
    this.refresh()
    this.openness = new MutationObserver(() => {
      const open = this.dropdown?.openValue
      if (this.wasOpen && !open) this.dispatch("closed")
      this.wasOpen = open
    })
    this.openness.observe(this.element, { attributeFilter: [ "data-dropdown-open-value" ] })
  }

  disconnect() {
    this.openness.disconnect()
  }

  refresh(event) {
    if (event && this.pickedTarget.contains(event.target)) {
      const original = this.options.find(input => input.value === event.target.value)
      if (original) original.checked = event.target.checked
    }

    if (event?.target.type === "radio" && event.target.checked) this.close()

    const picked = this.options.filter(input => input.checked)
    this.countTarget.firstElementChild.textContent = picked.length
    this.clearTarget.hidden = picked.length === 0
    this.chevronTarget.hidden = picked.length > 0
    this.chipTarget.toggleAttribute("data-applied", picked.length > 0)
    this.clearTarget.toggleAttribute("data-single", picked.length === 1)
    this.labelTarget.textContent = picked.length === 1 ? this.name(picked[0]) : this.labelTarget.dataset.default
    this.showPicked(picked)
  }

  clear() {
    const picked = this.options.filter(input => input.checked)
    picked.forEach(input => { input.checked = false })
    picked.at(-1)?.dispatchEvent(new Event("change", { bubbles: true }))
    this.refresh()
  }

  // Copies, not moves: the full list keeps every option in its place. The
  // copies carry no name, so a form only ever submits the originals.
  showPicked(picked) {
    const rows = picked.map(input => {
      const row = input.closest("label").cloneNode(true)
      row.querySelectorAll("input").forEach(copy => {
        copy.removeAttribute("name")
        copy.removeAttribute("id")
        copy.checked = copy.value === input.value
      })
      return row
    })
    this.pickedRowsTarget.replaceChildren(...rows)
    this.pickedTarget.hidden = rows.length === 0
  }

  close() {
    this.dropdown?.close()
    this.element.querySelector("[aria-haspopup]")?.setAttribute("aria-expanded", "false")
  }

  name(input) {
    return input.closest("label").querySelector(".truncate")?.textContent.trim() || this.labelTarget.dataset.default
  }

  get dropdown() {
    return this.application.getControllerForElementAndIdentifier(this.element, "dropdown")
  }

  get options() {
    return [...this.element.querySelectorAll("[role='menu'] input[type='checkbox'], [role='menu'] input[type='radio']")]
      .filter(input => !this.pickedTarget.contains(input))
  }
}
